import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Row, Col, Form, Button, Table, Badge,
  Alert, Spinner, InputGroup, Modal, Tab, Nav
} from 'react-bootstrap';
import {
  FaCheckCircle, FaPlus, FaEdit, FaTrash, FaSave, FaExclamationTriangle,
  FaMoneyCheckAlt, FaReceipt, FaCalculator, FaClipboardCheck
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { format } from 'date-fns';

interface TransactionClosingManagerProps {
  transaction: any;
  onUpdate: () => void;
}

interface PaymentReceipt {
  id?: string;
  payment_source: 'landlord' | 'buyer' | 'seller' | 'other' | 'agent' | 'broker';
  payer_name: string;
  payer_email?: string;
  payment_date: string;
  payment_method: 'check' | 'wire' | 'ach' | 'cash' | 'zelle' | 'credit_card';
  reference_number?: string;
  amount_received: number;
  payment_direction?: 'incoming' | 'outgoing';
  description?: string;
  status: 'pending' | 'deposited' | 'cleared' | 'bounced' | 'void';
}

interface CommissionDistribution {
  id?: string;
  gross_amount: number;
  agent_amount: number;
  broker_amount: number;
  transaction_fee: number;
  eo_fee: number;
  other_deductions: number;
  status: 'pending' | 'processed' | 'paid';
}

interface ClosingExpense {
  id?: string;
  description: string;
  amount: number;
  expense_type: string;
  vendor_name?: string;
  paid_by?: 'agent' | 'broker' | 'seller' | 'buyer';
  expense_date: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
}

const TransactionClosingManager: React.FC<TransactionClosingManagerProps> = ({
  transaction,
  onUpdate
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Financial data states
  const [paymentReceipts, setPaymentReceipts] = useState<PaymentReceipt[]>([]);
  const [commissionDistribution, setCommissionDistribution] = useState<CommissionDistribution | null>(null);
  const [closingExpenses, setClosingExpenses] = useState<ClosingExpense[]>([]);
  const [closingNotes, setClosingNotes] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isTableFunding, setIsTableFunding] = useState(false);
  const [savingInstructions, setSavingInstructions] = useState(false);
  
  // Commission data from agent profile and transaction
  const [commissionData, setCommissionData] = useState<any>(null);
  const [editableCommission, setEditableCommission] = useState<any>(null);
  const [showCommissionEditModal, setShowCommissionEditModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  
  // Form states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<PaymentReceipt | null>(null);
  const [editingExpense, setEditingExpense] = useState<ClosingExpense | null>(null);

  useEffect(() => {
    fetchClosingData();
  }, [transaction.id]);

  const fetchClosingData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 [TransactionClosing] Fetching closing data for transaction:', transaction.id);

      // Fetch all closing-related data in parallel
      const [receiptsRes, distributionRes, expensesRes, commissionRes, commissionOverrideRes, invoiceRes] = await Promise.all([
        // Get payment receipts for this transaction
        axiosInstance.get(`/transaction-closing/transactions/${transaction.id}/closing/receipts`)
          .catch(error => {
            console.error('❌ [TransactionClosing] Failed to fetch receipts:', error.response?.data || error.message);
            return { data: { receipts: [] } };
          }),
        
        // Get commission distribution data
        axiosInstance.get(`/transaction-closing/transactions/${transaction.id}/closing/distribution`)
          .catch(error => {
            console.error('❌ [TransactionClosing] Failed to fetch distribution:', error.response?.data || error.message);
            return { data: null };
          }),
        
        // Get closing expenses
        axiosInstance.get(`/transaction-closing/transactions/${transaction.id}/closing/expenses`)
          .catch(error => {
            console.error('❌ [TransactionClosing] Failed to fetch expenses:', error.response?.data || error.message);
            return { data: { expenses: [] } };
          }),
        
        // Get commission profile data for current agent/broker
        axiosInstance.get('/agent/commission-profile')
          .catch(error => {
            console.error('❌ [TransactionClosing] Failed to fetch commission profile:', error.response?.data || error.message);
            return { data: null };
          }),
        
        // Get saved commission overrides for this specific transaction
        axiosInstance.get(`/transaction-closing/transactions/${transaction.id}/commission-overrides`)
          .catch(error => {
            console.log('ℹ️ [TransactionClosing] No commission overrides found (will use agent profile defaults):', error.response?.data || error.message);
            return { data: { override: null } };
          }),
        
        // Get invoice data if rental
        transaction.type === 'rental' || transaction.type === 'lease' 
          ? axiosInstance.get(`/landlord-invoices?transaction_id=${transaction.id}`)
              .catch(error => {
                console.error('❌ [TransactionClosing] Failed to fetch invoice:', error.response?.data || error.message);
                return { data: { invoices: [] } };
              })
          : Promise.resolve({ data: { invoices: [] } })
      ]);

      // Safely process payment receipts with payment_direction defaulting
      const receipts = receiptsRes.data?.receipts || [];
      const processedReceipts = receipts.map(receipt => ({
        ...receipt,
        payment_direction: receipt.payment_direction || 'incoming', // Default to incoming if not set
        amount_received: Number(receipt.amount_received || 0), // Ensure numeric
        payment_date: receipt.payment_date || new Date().toISOString().split('T')[0] // Default date
      }));
      
      console.log('✅ [TransactionClosing] Processed receipts:', processedReceipts.length);
      setPaymentReceipts(processedReceipts);
      setCommissionDistribution(distributionRes.data?.distribution || null);
      
      // Safely process expenses
      const expenses = expensesRes.data?.expenses || [];
      const processedExpenses = expenses.map(expense => ({
        ...expense,
        amount: Number(expense.amount || 0), // Ensure numeric
        expense_date: expense.expense_date || new Date().toISOString().split('T')[0]
      }));
      setClosingExpenses(processedExpenses);
      
      // Process commission data - use saved overrides first, fallback to agent profile
      const savedOverride = commissionOverrideRes.data?.override;
      
      if (savedOverride) {
        // Use saved commission overrides
        console.log('✅ [TransactionClosing] Using saved commission overrides:', savedOverride.id);
        
        const commissionObj = {
          commission_type: savedOverride.commission_type || 'percentage',
          split_type: savedOverride.split_type || 'percentage',
          sale_price: parseFloat(savedOverride.sale_price || 0),
          commission_rate: parseFloat(savedOverride.commission_rate || 0),
          flat_fee_amount: parseFloat(savedOverride.flat_fee_amount || 0),
          commission_details: {
            gross_commission: parseFloat(savedOverride.gross_commission || 0),
            agent_split_percentage: parseFloat(savedOverride.agent_split_percentage || 0),
            broker_split_percentage: parseFloat(savedOverride.broker_split_percentage || 0),
            agent_gross_commission: parseFloat(savedOverride.agent_gross_commission || 0),
            broker_gross_commission: parseFloat(savedOverride.broker_gross_commission || 0),
            agent_net_commission: parseFloat(savedOverride.agent_net_commission || 0),
            broker_net_commission: parseFloat(savedOverride.broker_net_commission || 0),
            agent_flat_amount: parseFloat(savedOverride.agent_flat_amount || 0),
            broker_flat_amount: parseFloat(savedOverride.broker_flat_amount || 0)
          },
          fees: {
            transaction_fee: parseFloat(savedOverride.transaction_fee || 0),
            eo_fee: parseFloat(savedOverride.eo_fee || 0),
            monthly_fee: parseFloat(savedOverride.monthly_fee || 0)
          },
          agent_info: {
            name: transaction.agent_name || 'Unknown Agent',
            brokerage: 'KairosIQ AI Realty'
          },
          misc_fees: [] as any[] // Initialize with empty array, will be populated below
        };
        
        // Parse misc_fees (partner splits) from saved override
        let miscFees: any[] = [];
        if (savedOverride.misc_fees) {
          try {
            const parsed = typeof savedOverride.misc_fees === 'string'
              ? JSON.parse(savedOverride.misc_fees)
              : savedOverride.misc_fees;
            miscFees = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            console.error('Error parsing misc_fees:', e);
            miscFees = [];
          }
        }
        
        // Recalculate agent net commission to account for misc_fees paid by agent
        const agentMiscFees = miscFees.filter(fee => fee.paidBy === 'agent')
          .reduce((sum, fee) => sum + parseFloat(fee.amount || 0), 0);
        
        // Update the commission object with misc_fees data
        commissionObj.misc_fees = miscFees;
        // Agent net should be the gross minus ONLY the fees, NOT minus partner splits
        // Partner splits are still going to agents, just different agents
        commissionObj.commission_details.agent_net_commission = 
          commissionObj.commission_details.agent_gross_commission - 
          commissionObj.fees.eo_fee - 
          commissionObj.fees.transaction_fee;
        
        setCommissionData(commissionObj);
        setEditableCommission(JSON.parse(JSON.stringify(commissionObj))); // Deep copy for editing
        
      } else if (commissionRes.data?.success && commissionRes.data?.data) {
        // Fallback to agent profile defaults
        console.log('ℹ️ [TransactionClosing] No saved overrides found, using agent profile defaults');
        
        const profileData = commissionRes.data.data;
        const salePrice = transaction.property_data?.sale_price || transaction.property_data?.price || 0;
        const commissionRate = transaction.commission_rate || 2.5;
        const grossCommission = (salePrice * commissionRate) / 100;
        
        const commissionObj = {
          commission_type: 'percentage',
          split_type: 'percentage',
          sale_price: salePrice,
          commission_rate: commissionRate,
          flat_fee_amount: 0,
          commission_details: {
            gross_commission: grossCommission,
            agent_split_percentage: profileData.commissionSplitAgent || 80,
            broker_split_percentage: profileData.commissionSplitBroker || 20,
            agent_gross_commission: (grossCommission * (profileData.commissionSplitAgent || 80)) / 100,
            broker_gross_commission: (grossCommission * (profileData.commissionSplitBroker || 20)) / 100,
            agent_net_commission: ((grossCommission * (profileData.commissionSplitAgent || 80)) / 100) - (profileData.eoFee || 60) - (profileData.transactionFee || 0),
            broker_net_commission: (grossCommission * (profileData.commissionSplitBroker || 20)) / 100,
            agent_flat_amount: 0,
            broker_flat_amount: 0
          },
          fees: {
            transaction_fee: profileData.transactionFee || 0,
            eo_fee: profileData.eoFee || 60,
            monthly_fee: profileData.monthlyFee || 0
          },
          agent_info: {
            name: profileData.agentName,
            brokerage: profileData.brokerageName
          },
          misc_fees: [] // Initialize with empty array for agent profile defaults
        };
        
        setCommissionData(commissionObj);
        setEditableCommission(JSON.parse(JSON.stringify(commissionObj))); // Deep copy for editing
      }
      
      setInvoiceData(invoiceRes.data?.invoices?.[0] || null);
      
      // Load closing notes from transaction
      setClosingNotes(transaction.closing_notes || '');
      
      // Load special instructions from commission overrides
      const specialInst = commissionOverrideRes.data?.override?.special_instructions || '';
      const tableFunding = commissionOverrideRes.data?.override?.is_table_funding || false;
      setSpecialInstructions(specialInst);
      setIsTableFunding(tableFunding);

      console.log('✅ [TransactionClosing] All closing data loaded successfully');

    } catch (err) {
      console.error('❌ [TransactionClosing] Error fetching closing data:', err);
      setError('Failed to load closing data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSavePaymentReceipt = async (receipt: PaymentReceipt) => {
    try {
      setSaving(true);
      
      const endpoint = receipt.id 
        ? `/transaction-closing/payment-receipts/${receipt.id}`
        : '/transaction-closing/payment-receipts';
        
      const method = receipt.id ? 'put' : 'post';
      
      const payload = {
        ...receipt,
        transaction_id: transaction.id,
        invoice_id: invoiceData?.id,
        payment_type: 'commission',
        created_by: 'broker'
      };
      
      console.log('💾 [TransactionClosing] About to save payment:', {
        method,
        endpoint,
        payloadKeys: Object.keys(payload),
        transactionId: payload.transaction_id,
        direction: payload.payment_direction,
        amount: payload.amount_received
      });

      const response = await axiosInstance[method](endpoint, payload);
      console.log('✅ [TransactionClosing] Payment saved successfully:', response.data);
      
      setShowPaymentModal(false);
      setEditingPayment(null);
      await fetchClosingData();
      
    } catch (err) {
      console.error('❌ [TransactionClosing] Error saving payment receipt:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status,
        payload: err.config?.data
      });
      alert(`Failed to save payment receipt: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExpense = async (expense: ClosingExpense) => {
    try {
      setSaving(true);
      
      const endpoint = expense.id 
        ? `/transaction-closing/transactions/${transaction.id}/closing/expenses/${expense.id}`
        : `/transaction-closing/transactions/${transaction.id}/closing/expenses`;
        
      const method = expense.id ? 'put' : 'post';
      
      const payload = {
        ...expense,
        transaction_id: transaction.id
      };
      
      await axiosInstance[method](endpoint, payload);
      
      setShowExpenseModal(false);
      setEditingExpense(null);
      await fetchClosingData();
      
    } catch (err) {
      console.error('Error saving expense:', err);
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClosingNotes = async () => {
    try {
      setSaving(true);
      
      await axiosInstance.put(`/transactions/${transaction.id}`, {
        closing_notes: closingNotes
      });
      
      alert('Closing notes saved successfully');
      
    } catch (err) {
      console.error('Error saving closing notes:', err);
      alert('Failed to save closing notes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      setSaving(true);
      
      await axiosInstance.delete(`/transaction-closing/transactions/${transaction.id}/closing/expenses/${expenseId}`);
      
      console.log('✅ [TransactionClosing] Expense deleted successfully');
      await fetchClosingData(); // Refresh the data
      
    } catch (err) {
      console.error('❌ [TransactionClosing] Error deleting expense:', {
        error: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      alert(`Failed to delete expense: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsCompleted = async () => {
    // Validation checks
    const validationErrors = [];
    
    // Check if all payments are received
    if (totals.shortfall > 0) {
      validationErrors.push(`Outstanding payment of $${totals.shortfall.toLocaleString()}`);
    }
    
    // Check if commission data exists for purchase transactions
    if ((transaction.type === 'purchase' || transaction.type === 'listing') && !commissionData) {
      validationErrors.push('Commission Disbursement Authorization (CDA) not completed');
    }
    
    // Check if any payments are pending
    const pendingPayments = paymentReceipts.filter(r => r.status === 'pending');
    if (pendingPayments.length > 0) {
      validationErrors.push(`${pendingPayments.length} payment(s) still pending`);
    }
    
    if (validationErrors.length > 0) {
      alert(`Cannot complete transaction:\n\n${validationErrors.join('\n')}`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to mark this transaction as completed?\n\nThis will:\n- Lock the transaction from further edits\n- Create commission distribution records\n- Update financial tracking\n\nThis action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Update transaction stage
      await axiosInstance.put(`/transactions/${transaction.id}/stage`, {
        stage: 'completed'
      });
      
      // Create commission distribution if needed
      if (!commissionDistribution && commissionData) {
        await axiosInstance.post('/transaction-closing/commission-distributions', {
          transaction_id: transaction.id,
          payment_receipt_id: paymentReceipts[0]?.id,
          gross_amount: commissionData.commission_details?.gross_commission || 0,
          agent_amount: commissionData.commission_details?.agent_net_commission || 0,
          broker_amount: commissionData.commission_details?.broker_net_commission || 0,
          transaction_fee: commissionData.fees?.transaction_fee || 0,
          eo_fee: commissionData.fees?.eo_fee || 0
        });
      }
      
      // Create 1099 payment records
      if (commissionData && commissionData.commission_details?.agent_net_commission > 0) {
        await axiosInstance.post('/transaction-closing/agent-1099-payments', {
          tax_year: new Date().getFullYear(),
          agent_id: transaction.agent_id,
          brokerage_id: transaction.brokerage_id,
          payment_date: new Date().toISOString().split('T')[0],
          payment_type: 'commission',
          payment_description: `Commission for ${transaction.property_data?.address || 'Transaction ' + transaction.id}`,
          transaction_id: transaction.id,
          gross_amount: commissionData.commission_details.agent_gross_commission,
          net_amount: commissionData.commission_details.agent_net_commission,
          payment_method: paymentReceipts[0]?.payment_method || 'check',
          payment_reference: paymentReceipts[0]?.reference_number,
          included_in_1099: true
        });
      }
      
      alert('Transaction marked as completed successfully!');
      onUpdate();
      
    } catch (err) {
      console.error('Error completing transaction:', err);
      alert('Failed to complete transaction');
    } finally {
      setSaving(false);
    }
  };

  const calculateTotals = () => {
    // Only count INCOMING payments as "Total Received"
    const totalReceived = paymentReceipts
      .filter(r => r.payment_direction === 'incoming')
      .reduce((sum, r) => sum + Number(r.amount_received || 0), 0);
    
    // Count OUTGOING payments as expenses (money paid out)
    const totalPaidOut = paymentReceipts
      .filter(r => r.payment_direction === 'outgoing')
      .reduce((sum, r) => sum + Number(r.amount_received || 0), 0);
    
    const totalExpenses = closingExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const expectedCommission = commissionData?.commission_details?.gross_commission || 0;
    
    // Net balance = incoming - (outgoing + expenses)
    const balance = totalReceived - (totalPaidOut + totalExpenses);
    
    return {
      totalReceived,
      totalPaidOut,
      totalExpenses,
      expectedCommission,
      balance,
      shortfall: expectedCommission - totalReceived
    };
  };
  
  const handleSaveCommissionToDB = async (updatedCommission: any) => {
    try {
      console.log('💾 [TransactionClosing] Saving commission overrides to database...');
      
      const payload = {
        commission_type: updatedCommission.commission_type || 'percentage',
        sale_price: updatedCommission.sale_price,
        commission_rate: updatedCommission.commission_rate,
        flat_fee_amount: updatedCommission.flat_fee_amount,
        split_type: updatedCommission.split_type || 'percentage',
        agent_split_percentage: updatedCommission.commission_details?.agent_split_percentage,
        broker_split_percentage: updatedCommission.commission_details?.broker_split_percentage,
        agent_flat_amount: updatedCommission.commission_details?.agent_flat_amount,
        broker_flat_amount: updatedCommission.commission_details?.broker_flat_amount,
        transaction_fee: updatedCommission.fees?.transaction_fee,
        eo_fee: updatedCommission.fees?.eo_fee,
        monthly_fee: updatedCommission.fees?.monthly_fee,
        gross_commission: updatedCommission.commission_details?.gross_commission,
        agent_gross_commission: updatedCommission.commission_details?.agent_gross_commission,
        broker_gross_commission: updatedCommission.commission_details?.broker_gross_commission,
        agent_net_commission: updatedCommission.commission_details?.agent_net_commission,
        broker_net_commission: updatedCommission.commission_details?.broker_net_commission
      };
      
      console.log('💾 [TransactionClosing] Commission payload:', {
        transaction_id: transaction.id,
        commission_type: payload.commission_type,
        sale_price: payload.sale_price,
        gross_commission: payload.gross_commission,
        agent_net: payload.agent_net_commission,
        broker_net: payload.broker_net_commission
      });

      const response = await axiosInstance.post(`/transaction-closing/transactions/${transaction.id}/commission-overrides`, payload);
      
      console.log('✅ [TransactionClosing] Commission overrides saved successfully:', response.data);
      alert('Commission details saved successfully!');
      
    } catch (error) {
      console.error('❌ [TransactionClosing] Error saving commission overrides:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(`Failed to save commission changes: ${error.response?.data?.error || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading closing data...</p>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="transaction-closing-manager">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted mb-1">Expected Commission</h5>
              <h3 className="text-primary">${totals.expectedCommission.toLocaleString()}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted mb-1">Total Received</h5>
              <h3 className="text-success">${totals.totalReceived.toLocaleString()}</h3>
              <small className="text-muted">Incoming payments only</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted mb-1">Total Paid Out</h5>
              <h3 className="text-danger">${totals.totalPaidOut.toLocaleString()}</h3>
              <small className="text-muted">Outgoing payments</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted mb-1">Expenses</h5>
              <h3 className="text-warning">${totals.totalExpenses.toLocaleString()}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-muted mb-1">Brokerage Earnings</h5>
              <h3 className={totals.balance >= 0 ? 'text-success' : 'text-danger'}>
                ${totals.balance.toLocaleString()}
              </h3>
              <small className="text-muted">Firm profit</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Alert */}
      {totals.shortfall > 0 && (
        <Alert variant="warning">
          <FaExclamationTriangle className="me-2" />
          Outstanding balance: ${totals.shortfall.toLocaleString()} still to be received
        </Alert>
      )}

      {/* Tab Navigation */}
      <Tab.Container activeKey={activeTab}>
        <Card>
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="overview" onClick={() => setActiveTab('overview')}>
                  <FaClipboardCheck className="me-2" />
                  Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="payments" onClick={() => setActiveTab('payments')}>
                  <FaMoneyCheckAlt className="me-2" />
                  Payment Receipts ({paymentReceipts.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="expenses" onClick={() => setActiveTab('expenses')}>
                  <FaReceipt className="me-2" />
                  Expenses ({closingExpenses.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="commission" onClick={() => setActiveTab('commission')}>
                  <FaCalculator className="me-2" />
                  Commission Details
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                <h5>Transaction Closing Summary</h5>
                <Row className="mb-4">
                  <Col md={6}>
                    <Table bordered size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Transaction Type</strong></td>
                          <td>{transaction.type}</td>
                        </tr>
                        <tr>
                          <td><strong>Property Address</strong></td>
                          <td>{transaction.property_data?.address || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Client</strong></td>
                          <td>{transaction.client_name}</td>
                        </tr>
                        <tr>
                          <td><strong>Agent</strong></td>
                          <td>{transaction.agent_name || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Current Stage</strong></td>
                          <td>
                            <Badge bg={transaction.stage === 'completed' ? 'success' : 'warning'}>
                              {transaction.stage}
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <h6>Closing Notes</h6>
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={closingNotes}
                        onChange={(e) => setClosingNotes(e.target.value)}
                        placeholder="Add any important closing notes here..."
                      />
                    </Form.Group>
                    <Button 
                      variant="primary" 
                      size="sm" 
                      className="mt-2"
                      onClick={handleSaveClosingNotes}
                      disabled={saving}
                    >
                      <FaSave className="me-1" />
                      Save Notes
                    </Button>
                  </Col>
                </Row>

                {/* Completion Action */}
                {transaction.stage !== 'completed' && (
                  <div className="text-center mt-4 p-4 bg-light rounded">
                    <h5>Ready to Close?</h5>
                    <p>Ensure all payments have been received and recorded before marking as completed.</p>
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={handleMarkAsCompleted}
                      disabled={saving || totals.shortfall > 0}
                    >
                      <FaCheckCircle className="me-2" />
                      Mark Transaction as Completed
                    </Button>
                    {totals.shortfall > 0 && (
                      <p className="text-danger mt-2">
                        Cannot complete: Outstanding balance of ${totals.shortfall.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </Tab.Pane>

              {/* Payment Receipts Tab */}
              <Tab.Pane eventKey="payments">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Payment Receipts</h5>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setEditingPayment(null);
                      setShowPaymentModal(true);
                    }}
                  >
                    <FaPlus className="me-1" />
                    Record Payment
                  </Button>
                </div>

                {paymentReceipts.length === 0 ? (
                  <Alert variant="info">
                    No payments recorded yet. Click "Record Payment" to add payment receipts.
                  </Alert>
                ) : (
                  <>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Direction</th>
                          <th>Payer/Payee</th>
                          <th>Method</th>
                          <th>Reference #</th>
                          <th>Amount</th>
                          <th>Running Balance</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentReceipts.map((receipt, index) => {
                          // Calculate running balance
                          const previousBalance = paymentReceipts
                            .slice(0, index)
                            .reduce((sum, r) => {
                              const amount = Number(r.amount_received);
                              return sum + (r.payment_direction === 'incoming' ? amount : -amount);
                            }, 0);
                          const currentBalance = previousBalance + 
                            (receipt.payment_direction === 'incoming' ? Number(receipt.amount_received) : -Number(receipt.amount_received));
                          
                          return (
                            <tr key={receipt.id || index}>
                              <td>{receipt.payment_date ? format(new Date(receipt.payment_date), 'MM/dd/yyyy') : '-'}</td>
                              <td>
                                <Badge bg={receipt.payment_direction === 'incoming' ? 'success' : 'warning'}>
                                  {receipt.payment_direction === 'incoming' ? 'IN' : 'OUT'}
                                </Badge>
                              </td>
                              <td>{receipt.payer_name}</td>
                              <td>{receipt.payment_method.toUpperCase()}</td>
                              <td>{receipt.reference_number || '-'}</td>
                              <td className={`text-end ${receipt.payment_direction === 'outgoing' ? 'text-danger' : 'text-success'}`}>
                                {receipt.payment_direction === 'incoming' ? '+' : '-'}${Number(receipt.amount_received).toLocaleString()}
                              </td>
                              <td className="text-end fw-bold">${currentBalance.toLocaleString()}</td>
                              <td>
                                <Badge bg={
                                  receipt.status === 'cleared' ? 'success' :
                                  receipt.status === 'deposited' ? 'info' :
                                  receipt.status === 'bounced' ? 'danger' : 'warning'
                                }>
                                  {receipt.status}
                                </Badge>
                              </td>
                              <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => {
                                setEditingPayment(receipt);
                                setShowPaymentModal(true);
                              }}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="me-1"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete this ${receipt.payment_direction === 'incoming' ? 'incoming' : 'outgoing'} payment of $${receipt.amount_received?.toLocaleString()}?`)) {
                                  try {
                                    await axiosInstance.delete(`/transaction-closing/payment-receipts/${receipt.id}`);
                                    await fetchClosingData();
                                  } catch (err) {
                                    alert('Failed to delete payment receipt');
                                  }
                                }
                              }}
                            >
                              <FaTrash />
                            </Button>
                            {receipt.status === 'pending' && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm('Mark this payment as deposited?')) {
                                    try {
                                      await axiosInstance.put(`/transaction-closing/payment-receipts/${receipt.id}`, {
                                        ...receipt,
                                        status: 'deposited',
                                        deposited_date: new Date().toISOString().split('T')[0]
                                      });
                                      await fetchClosingData();
                                    } catch (err) {
                                      alert('Failed to update payment status');
                                    }
                                  }
                                }}
                              >
                                Mark Deposited
                              </Button>
                            )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                    
                    {/* Summary Section */}
                    <div className="mt-3 p-3 bg-light rounded">
                      <Row>
                        <Col>
                          <strong>Total Incoming:</strong> ${
                            paymentReceipts
                              .filter(r => r.payment_direction === 'incoming')
                              .reduce((sum, r) => sum + Number(r.amount_received), 0)
                              .toLocaleString()
                          }
                        </Col>
                        <Col>
                          <strong>Total Outgoing:</strong> ${
                            paymentReceipts
                              .filter(r => r.payment_direction === 'outgoing')
                              .reduce((sum, r) => sum + Number(r.amount_received), 0)
                              .toLocaleString()
                          }
                        </Col>
                        <Col>
                          <strong>Net Balance:</strong> ${
                            paymentReceipts.reduce((sum, r) => {
                              const amount = Number(r.amount_received);
                              return sum + (r.payment_direction === 'incoming' ? amount : -amount);
                            }, 0).toLocaleString()
                          }
                        </Col>
                      </Row>
                    </div>
                  </>
                )}
              </Tab.Pane>

              {/* Expenses Tab */}
              <Tab.Pane eventKey="expenses">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Closing Expenses</h5>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setEditingExpense(null);
                      setShowExpenseModal(true);
                    }}
                  >
                    <FaPlus className="me-1" />
                    Add Expense
                  </Button>
                </div>

                {closingExpenses.length === 0 ? (
                  <Alert variant="info">
                    No expenses recorded yet. Click "Add Expense" to track closing costs.
                  </Alert>
                ) : (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Category</th>
                        <th>Paid By</th>
                        <th>Amount</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closingExpenses.map((expense, index) => (
                        <tr key={expense.id || index}>
                          <td>{expense.expense_date ? format(new Date(expense.expense_date), 'MM/dd/yyyy') : '-'}</td>
                          <td>{expense.description}</td>
                          <td>{expense.expense_type || '-'}</td>
                          <td>{expense.vendor_name || expense.paid_by || '-'}</td>
                          <td className="text-end">${Number(expense.amount).toLocaleString()}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => {
                                setEditingExpense(expense);
                                setShowExpenseModal(true);
                              }}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete the expense "${expense.description}"?`)) {
                                  handleDeleteExpense(expense.id);
                                }
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab.Pane>

              {/* Commission Details Tab */}
              <Tab.Pane eventKey="commission">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Commission & Distribution Details</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowCommissionEditModal(true)}
                  >
                    <FaEdit className="me-1" />
                    Edit Commission
                  </Button>
                </div>
                
                {commissionData ? (
                  <>
                    <Row>
                      <Col md={6}>
                        <h6>Commission Breakdown</h6>
                        <Table bordered>
                          <tbody>
                            <tr>
                              <td>Gross Commission</td>
                              <td className="text-end">${commissionData.commission_details?.gross_commission?.toLocaleString() || 0}</td>
                            </tr>
                            <tr>
                              <td>Agent Split ({commissionData.commission_details?.agent_split_percentage || 0}%)</td>
                              <td className="text-end">${commissionData.commission_details?.agent_gross_commission?.toLocaleString() || 0}</td>
                            </tr>
                            <tr>
                              <td>Transaction Fee</td>
                              <td className="text-end">-${commissionData.fees?.transaction_fee?.toLocaleString() || 0}</td>
                            </tr>
                            <tr>
                              <td>E&O Fee</td>
                              <td className="text-end">-${commissionData.fees?.eo_fee?.toLocaleString() || 0}</td>
                            </tr>
                            {/* Show misc fees (partner splits) */}
                            {commissionData.misc_fees?.filter(fee => fee.paidBy === 'agent').map((fee, idx) => (
                              <tr key={idx}>
                                <td>{fee.description}</td>
                                <td className="text-end">-${parseFloat(fee.amount || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                            <tr>
                              <td><strong>Total Agent Payout</strong></td>
                              <td className="text-end"><strong>${commissionData.commission_details?.agent_net_commission?.toLocaleString() || 0}</strong></td>
                            </tr>
                            <tr>
                              <td><strong>Broker Net</strong></td>
                              <td className="text-end"><strong>${commissionData.commission_details?.broker_net_commission?.toLocaleString() || 0}</strong></td>
                            </tr>
                          </tbody>
                        </Table>
                      </Col>
                      <Col md={6}>
                        {invoiceData && (
                          <>
                            <h6>Invoice Information</h6>
                            <Table bordered>
                              <tbody>
                                <tr>
                                  <td>Invoice #</td>
                                  <td>{invoiceData.invoice_number}</td>
                                </tr>
                                <tr>
                                  <td>Invoice Date</td>
                                  <td>{format(new Date(invoiceData.invoice_date), 'MM/dd/yyyy')}</td>
                                </tr>
                                <tr>
                                  <td>Total Amount</td>
                                  <td className="text-end">${invoiceData.total_amount?.toLocaleString()}</td>
                                </tr>
                                <tr>
                                  <td>Status</td>
                                  <td>
                                    <Badge bg={invoiceData.status === 'paid' ? 'success' : 'warning'}>
                                      {invoiceData.status}
                                    </Badge>
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </>
                        )}
                      </Col>
                    </Row>
                    
                    {commissionData.commission_details?.agent_net_commission > 0 && (
                      <div className="mt-4 p-3 bg-light rounded">
                        <h6>Record Commission Payment to Agent</h6>
                        <p className="text-muted mb-3">
                          When you pay the agent their commission, record it here to track outgoing funds.
                        </p>
                        <Button
                          variant="primary"
                          onClick={() => {
                            // Pre-fill the payment modal with agent payment info
                            setEditingPayment({
                              payment_direction: 'outgoing',
                              payment_source: 'agent',
                              payer_name: transaction.agent_name || 'Agent',
                              payment_date: new Date().toISOString().split('T')[0],
                              payment_method: 'check',
                              amount_received: commissionData.commission_details?.agent_net_commission || 0,
                              description: `Commission payment to ${transaction.agent_name || 'Agent'}`,
                              status: 'pending'
                            });
                            setShowPaymentModal(true);
                          }}
                        >
                          <FaMoneyCheckAlt className="me-2" />
                          Record Agent Payment (${commissionData.commission_details?.agent_net_commission?.toLocaleString() || 0})
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <Alert variant="info">
                    No commission authorization found. Please complete the CDA form in the Commission tab.
                  </Alert>
                )}
                
                {/* Special Instructions Section */}
                <div className="mt-4 p-3 border rounded">
                  <h6>Special Instructions</h6>
                  <p className="text-muted">Add wire transfer instructions or other payment details for escrow</p>
                  
                  <Form.Group className="mb-3">
                    <Form.Check 
                      type="checkbox"
                      id="table-funding"
                      label="Table Funding (wire transfer required at closing)"
                      checked={isTableFunding}
                      onChange={(e) => {
                        setIsTableFunding(e.target.checked);
                        // Auto-save on change
                        setSavingInstructions(true);
                        axiosInstance.put(`/transactions/${transaction.id}/special-instructions`, {
                          special_instructions: specialInstructions,
                          is_table_funding: e.target.checked
                        })
                        .then(() => {
                          console.log('✅ Table funding preference saved');
                        })
                        .catch(err => {
                          console.error('❌ Failed to save table funding:', err);
                        })
                        .finally(() => {
                          setSavingInstructions(false);
                        });
                      }}
                    />
                  </Form.Group>
                  
                  <Form.Group>
                    <Form.Label>Wire Transfer Instructions / Special Payment Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                      onBlur={() => {
                        // Auto-save on blur
                        setSavingInstructions(true);
                        axiosInstance.put(`/transactions/${transaction.id}/special-instructions`, {
                          special_instructions: specialInstructions,
                          is_table_funding: isTableFunding
                        })
                        .then(() => {
                          console.log('✅ Special instructions saved');
                        })
                        .catch(err => {
                          console.error('❌ Failed to save special instructions:', err);
                          alert('Failed to save special instructions');
                        })
                        .finally(() => {
                          setSavingInstructions(false);
                        });
                      }}
                      placeholder="Enter wire transfer instructions, bank details, or other special payment instructions for escrow..."
                    />
                  </Form.Group>
                  {savingInstructions && (
                    <div className="text-muted mt-2">
                      <Spinner animation="border" size="sm" /> Saving instructions...
                    </div>
                  )}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>

      {/* Payment Receipt Modal */}
      <PaymentReceiptModal
        show={showPaymentModal}
        onHide={() => {
          setShowPaymentModal(false);
          setEditingPayment(null);
        }}
        payment={editingPayment}
        onSave={handleSavePaymentReceipt}
        saving={saving}
      />

      {/* Expense Modal */}
      <ExpenseModal
        show={showExpenseModal}
        onHide={() => {
          setShowExpenseModal(false);
          setEditingExpense(null);
        }}
        expense={editingExpense}
        onSave={handleSaveExpense}
        saving={saving}
      />
      
      {/* Commission Edit Modal */}
      <CommissionEditModal
        show={showCommissionEditModal}
        onHide={() => setShowCommissionEditModal(false)}
        commissionData={editableCommission}
        transaction={transaction}
        onSave={async (updatedCommission) => {
          setEditableCommission(updatedCommission);
          setCommissionData(updatedCommission);
          setShowCommissionEditModal(false);
          await handleSaveCommissionToDB(updatedCommission);
        }}
      />
    </div>
  );
};

// Payment Receipt Modal Component
const PaymentReceiptModal: React.FC<{
  show: boolean;
  onHide: () => void;
  payment: PaymentReceipt | null;
  onSave: (payment: PaymentReceipt) => void;
  saving: boolean;
}> = ({ show, onHide, payment, onSave, saving }) => {
  const [formData, setFormData] = useState<PaymentReceipt>({
    payment_source: 'buyer',
    payer_name: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'check',
    amount_received: 0,
    payment_direction: 'incoming',
    status: 'pending'
  });

  useEffect(() => {
    if (payment) {
      setFormData({
        ...payment,
        payment_direction: payment.payment_direction || 'incoming'
      });
    } else {
      setFormData({
        payment_source: 'buyer',
        payer_name: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        amount_received: 0,
        payment_direction: 'incoming',
        status: 'pending'
      });
    }
  }, [payment]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {payment ? 'Edit Payment Receipt' : 
           formData.payment_direction === 'incoming' ? 'Record Incoming Payment' : 'Record Outgoing Payment'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Direction</Form.Label>
                <Form.Select
                  value={formData.payment_direction || 'incoming'}
                  onChange={(e) => {
                    const direction = e.target.value as 'incoming' | 'outgoing';
                    setFormData({ 
                      ...formData, 
                      payment_direction: direction,
                      // Auto-set payment source based on direction
                      payment_source: direction === 'outgoing' ? 'agent' : formData.payment_source
                    });
                  }}
                  required
                >
                  <option value="incoming">Incoming (From Client)</option>
                  <option value="outgoing">Outgoing (To Agent/Vendor)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Source</Form.Label>
                <Form.Select
                  value={formData.payment_source}
                  onChange={(e) => setFormData({ ...formData, payment_source: e.target.value as any })}
                  required
                >
                  {formData.payment_direction === 'incoming' ? (
                    <>
                      <option value="buyer">Buyer</option>
                      <option value="seller">Seller</option>
                      <option value="landlord">Landlord</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="agent">Agent</option>
                      <option value="broker">Broker</option>
                      <option value="other">Other Vendor</option>
                    </>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{formData.payment_direction === 'incoming' ? 'Payer Name' : 'Payee Name'}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.payer_name}
                  onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
                  placeholder={formData.payment_direction === 'incoming' ? "Enter payer's full name" : "Enter payee's full name"}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value as any })}
                  required
                >
                  <option value="check">Check</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="cash">Cash</option>
                  <option value="zelle">Zelle</option>
                  <option value="credit_card">Credit Card</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Reference Number</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.reference_number || ''}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Check #, Wire ref, etc."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{formData.payment_direction === 'incoming' ? 'Amount Received' : 'Amount Paid'}</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.amount_received}
                    onChange={(e) => setFormData({ ...formData, amount_received: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="deposited">Deposited</option>
                  <option value="cleared">Cleared</option>
                  <option value="bounced">Bounced</option>
                  <option value="void">Void</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional notes about this payment"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Payer Email (Optional)</Form.Label>
            <Form.Control
              type="email"
              value={formData.payer_email || ''}
              onChange={(e) => setFormData({ ...formData, payer_email: e.target.value })}
              placeholder="payer@email.com"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save Payment'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Expense Modal Component
const ExpenseModal: React.FC<{
  show: boolean;
  onHide: () => void;
  expense: ClosingExpense | null;
  onSave: (expense: ClosingExpense) => void;
  saving: boolean;
}> = ({ show, onHide, expense, onSave, saving }) => {
  const [formData, setFormData] = useState<ClosingExpense>({
    description: '',
    amount: 0,
    expense_type: 'closing_costs',
    paid_by: 'broker',
    expense_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (expense) {
      setFormData(expense);
    } else {
      setFormData({
        description: '',
        amount: 0,
        expense_type: 'closing_costs',
        paid_by: 'broker',
        expense_date: new Date().toISOString().split('T')[0]
      });
    }
  }, [expense]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {expense ? 'Edit Expense' : 'Add Closing Expense'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the expense"
              required
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={formData.expense_type}
                  onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                  required
                >
                  <option value="closing_costs">Closing Costs</option>
                  <option value="title_fees">Title Fees</option>
                  <option value="recording_fees">Recording Fees</option>
                  <option value="inspection">Inspection</option>
                  <option value="photography">Photography</option>
                  <option value="marketing">Marketing</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Paid By</Form.Label>
                <Form.Select
                  value={formData.paid_by}
                  onChange={(e) => setFormData({ ...formData, paid_by: e.target.value as any })}
                  required
                >
                  <option value="agent">Agent</option>
                  <option value="broker">Broker</option>
                  <option value="seller">Seller</option>
                  <option value="buyer">Buyer</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Vendor Name (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={formData.vendor_name || ''}
              onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
              placeholder="Enter vendor or company name"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional details about this expense"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : 'Save Expense'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// Commission Edit Modal — rewritten July 25, 2026
// All inputs are string state so decimals type naturally. Parse to numbers only on save.
const CommissionEditModal: React.FC<{
  show: boolean;
  onHide: () => void;
  commissionData: any;
  transaction: any;
  onSave: (commission: any) => void;
}> = ({ show, onHide, commissionData, transaction, onSave }) => {
  const [f, setF] = useState<Record<string, string>>({});
  const [commissionType, setCommissionType] = useState('percentage');
  const [splitType, setSplitType] = useState('percentage');

  // Reset form when modal opens with new data
  useEffect(() => {
    if (commissionData && show) {
      const d = commissionData;
      setCommissionType(d.commission_type || 'percentage');
      setSplitType(d.split_type || 'percentage');
      setF({
        sale_price: String(d.sale_price || 0),
        commission_rate: String(d.commission_rate ?? 2.5),
        flat_fee_amount: String(d.flat_fee_amount || 0),
        agent_split_pct: String(d.commission_details?.agent_split_percentage || 80),
        agent_flat_amount: String(d.commission_details?.agent_flat_amount || 0),
        broker_flat_amount: String(d.commission_details?.broker_flat_amount || 0),
        transaction_fee: String(d.fees?.transaction_fee || 0),
        eo_fee: String(d.fees?.eo_fee || 0),
        monthly_fee: String(d.fees?.monthly_fee || 0),
      });
    }
  }, [commissionData, show]);

  if (!commissionData) return null;

  const num = (key: string) => parseFloat(f[key] || '0') || 0;
  const set = (key: string, val: string) => setF(prev => ({ ...prev, [key]: val }));

  // Live calculations
  const grossCommission = commissionType === 'flat_fee'
    ? num('flat_fee_amount')
    : num('sale_price') * (num('commission_rate') / 100);

  const agentSplitPct = num('agent_split_pct');
  const brokerSplitPct = 100 - agentSplitPct;

  let agentGross: number, brokerGross: number;
  if (splitType === 'percentage') {
    agentGross = grossCommission * (agentSplitPct / 100);
    brokerGross = grossCommission * (brokerSplitPct / 100);
  } else {
    agentGross = num('agent_flat_amount');
    brokerGross = num('broker_flat_amount');
  }
  const agentNet = agentGross - num('transaction_fee') - num('eo_fee');

  const fmt = (n: number) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSave = () => {
    onSave({
      ...commissionData,
      commission_type: commissionType,
      split_type: splitType,
      sale_price: num('sale_price'),
      commission_rate: num('commission_rate'),
      flat_fee_amount: num('flat_fee_amount'),
      fees: { transaction_fee: num('transaction_fee'), eo_fee: num('eo_fee'), monthly_fee: num('monthly_fee') },
      commission_details: {
        ...commissionData.commission_details,
        gross_commission: grossCommission,
        agent_split_percentage: agentSplitPct,
        broker_split_percentage: brokerSplitPct,
        agent_flat_amount: num('agent_flat_amount'),
        broker_flat_amount: num('broker_flat_amount'),
        agent_gross_commission: agentGross,
        broker_gross_commission: brokerGross,
        agent_net_commission: agentNet,
        broker_net_commission: brokerGross,
      }
    });
  };

  const isRental = transaction.type === 'rental' || transaction.type === 'lease';

  // Simple text input that accepts decimals
  const field = (label: string, key: string, prefix?: string, suffix?: string, opts?: { disabled?: boolean; hint?: string }) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        {prefix && <InputGroup.Text>{prefix}</InputGroup.Text>}
        <Form.Control
          type="text"
          inputMode="decimal"
          value={opts?.disabled ? String(brokerSplitPct) : (f[key] ?? '')}
          onChange={(e) => !opts?.disabled && set(key, e.target.value)}
          disabled={opts?.disabled}
          placeholder="0"
        />
        {suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}
      </InputGroup>
      {opts?.hint && <Form.Text className="text-muted">{opts.hint}</Form.Text>}
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Commission Calculation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <FaExclamationTriangle className="me-2" />
          These changes only affect this transaction.
        </Alert>

        <h6 className="mb-3">Commission Structure</h6>
        <Form.Group className="mb-3">
          <Form.Label>Commission Type</Form.Label>
          <Form.Select value={commissionType} onChange={(e) => setCommissionType(e.target.value)}>
            <option value="percentage">Percentage of {isRental ? 'Lease' : 'Sale'} Price</option>
            <option value="flat_fee">Flat Fee Amount</option>
          </Form.Select>
        </Form.Group>

        <Row className="mb-4">
          {commissionType !== 'flat_fee' ? (
            <>
              <Col md={6}>{field(isRental ? 'Lease Price' : 'Sale Price', 'sale_price', '$')}</Col>
              <Col md={6}>{field('Commission Rate', 'commission_rate', undefined, '%')}</Col>
            </>
          ) : (
            <Col md={12}>{field('Flat Commission Amount', 'flat_fee_amount', '$', undefined, { hint: 'Total commission for this transaction' })}</Col>
          )}
        </Row>

        <h6 className="mb-3">Commission Distribution</h6>
        <Form.Group className="mb-3">
          <Form.Label>Split Type</Form.Label>
          <Form.Select value={splitType} onChange={(e) => setSplitType(e.target.value)}>
            <option value="percentage">Percentage Split</option>
            <option value="flat">Flat Amount Split</option>
          </Form.Select>
        </Form.Group>

        <Row className="mb-4">
          {splitType === 'percentage' ? (
            <>
              <Col md={6}>{field('Agent Split', 'agent_split_pct', undefined, '%')}</Col>
              <Col md={6}>{field('Broker Split', 'broker_split_pct', undefined, '%', { disabled: true, hint: '100% minus Agent Split' })}</Col>
            </>
          ) : (
            <>
              <Col md={6}>{field('Agent Amount', 'agent_flat_amount', '$')}</Col>
              <Col md={6}>{field('Broker Amount', 'broker_flat_amount', '$')}</Col>
            </>
          )}
        </Row>

        <h6 className="mb-3">Fees & Deductions</h6>
        <Row>
          <Col md={4}>{field('Transaction Fee', 'transaction_fee', '$')}</Col>
          <Col md={4}>{field('E&O Insurance', 'eo_fee', '$')}</Col>
          <Col md={4}>{field('Monthly Fee', 'monthly_fee', '$')}</Col>
        </Row>

        <Card className="mt-3 bg-light">
          <Card.Body className="py-2">
            <Row className="text-center">
              <Col><small className="text-muted d-block">Gross</small><strong>{fmt(grossCommission)}</strong></Col>
              <Col><small className="text-muted d-block">Agent Gross</small><strong>{fmt(agentGross)}</strong></Col>
              <Col><small className="text-muted d-block">Agent Net</small><strong className="text-success">{fmt(agentNet)}</strong></Col>
              <Col><small className="text-muted d-block">Broker</small><strong>{fmt(brokerGross)}</strong></Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave}><FaSave className="me-1" />Save Changes</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionClosingManager;